import { axiosInstance } from "@retro-ia/shared/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatMessage } from "../retro-api/useRetroChatStream";

export interface ActividadAprendiz {
  id: number;
  idActividad: number;
  idAprendiz: number;
  intentos: number;
  intentosExtras?: number;
  historialChat?:
    | {
        role: string;
        content: string;
      }[]
    | null;
  creadoPor?: string;
  actualizadoPor?: string;
  ultimoIntento?: {
    id: number;
    retro: string;
    sessionId: string;
    sessionPayload: string;
    preguntasSugeridas?: string[] | null;
    preguntasUsadas?: string[] | null;
  };
  actividadPrograma?: {
    idPrograma: number;
    programa?: {
      limiteIntentos: number;
    };
  };
}

export function useActividadAprendiz(
  idActividad: number,
  idAprendiz: number | undefined,
) {
  return useQuery<ActividadAprendiz | null>({
    queryKey: ["actividadAprendiz", idActividad, idAprendiz],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(
          `/actividades-aprendiz/actividad/${idActividad}/aprendiz/${idAprendiz}`
        );
        return response.data;
      } catch (error: any) {
        // Si es un 404 (actividad no asignada al aprendiz), retornar null
        if (error?.response?.status === 404) {
          return null;
        }
        // Para otros errores, propagarlos
        throw error;
      }
    },
    enabled: idAprendiz != null,
    staleTime: 30_000, // 30 segundos de datos frescos para evitar refetches innecesarios
    refetchOnWindowFocus: true, // Refetch cuando la ventana recupera el foco
    refetchOnMount: true, // Refetch cuando el componente se monta
    retry: false, // No reintentar en caso de error 404
  });
}

export function useUpdateActividadAprendiz() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idActividad,
      historialChat,
    }: {
      idActividad: number;
      historialChat: ChatMessage[];
    }) => {
      const response = await axiosInstance.patch(
        `/actividades-aprendiz/${idActividad}`,
        { historialChat },
      );
      return response.data;
    },
    onSuccess: (_data, _variables) => {
      // Invalidar la query para refrescar los datos
      // Necesitamos obtener idActividad e idAprendiz de alguna manera
      // Por ahora, invalidamos todas las queries de actividadAprendiz
      queryClient.invalidateQueries({
        queryKey: ["actividadAprendiz"],
      });
    },
  });
}
